import { useState, useEffect, useCallback } from 'react';
import { languageDetectionService, UserLanguageContext } from '../services/languageDetectionService';

export interface UseLanguageDetectionReturn {
  languageContext: UserLanguageContext | null;
  isLoading: boolean;
  error: string | null;
  refreshLanguageDetection: () => Promise<void>;
  setManualLanguage: (languageCode: string, languageId: string) => void;
  getCurrentLanguageCategoryId: () => string | null;
  getCurrentLanguageCode: () => string | null;
  isInitialized: boolean;
}

export const useLanguageDetection = (): UseLanguageDetectionReturn => {
  const [languageContext, setLanguageContext] = useState<UserLanguageContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // 🔧 MOBILE FIX: Enhanced mobile and localhost detection
  const isMobile = typeof window !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
    window.innerWidth <= 768
  );

  // 🏠 LOCALHOST FIX: Detect localhost environment
  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('192.168.') ||
    window.location.hostname.includes('10.0.') ||
    window.location.port === '3000'
  );

  // Initialize language detection on component mount
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('🌍 useLanguageDetection: Initializing language detection...', { 
          isMobile, 
          isLocalhost,
          hostname: window.location.hostname,
          port: window.location.port 
        });
        
        // 🌍 UNIVERSAL: Let the language detection service handle all languages dynamically
        // No more hard-coded language checks - service will find best match from 100+ languages
        
        // 🔧 UNIFIED TIMEOUT: Same timeout for all environments to ensure consistent language detection
        let timeoutDuration: number = 4000; // 4s timeout for all environments
        
        console.log('🌍 Language detection timeout:', {
          timeoutDuration,
          isMobile,
          isLocalhost,
          environment: isLocalhost ? 'localhost' : 'production',
          deviceType: isMobile ? 'mobile' : 'desktop'
        });
        
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Language detection timeout')), timeoutDuration);
        });
        
        const context = await Promise.race([
          languageDetectionService.initializeLanguageDetection(),
          timeoutPromise
        ]) as any;
        
        setLanguageContext(context);
        setIsInitialized(true);
        
        console.log('🌍 useLanguageDetection: Language detection completed:', context);
        
        // 🔧 MOBILE DEBUG: Extra logging for mobile troubleshooting
        if (isMobile) {
          console.log('📱 Mobile language detection:', {
            deviceType: 'mobile',
            environment: isLocalhost ? 'localhost' : 'production',
            languageId: context?.selectedLanguage?.language_id,
            languageCode: context?.selectedLanguage?.language_code,
            browserLanguages: navigator.languages || [navigator.language]
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Language detection failed';
        setError(errorMessage);
        console.error('❌ useLanguageDetection: Language detection failed:', err);
        
        // 🌍 UNIVERSAL FALLBACK: Use service fallback logic instead of hard-coded languages
        try {
          // Try to get fallback from service
          const browserLanguages = navigator.languages ? 
            Array.from(navigator.languages) : [navigator.language || 'en'];
          
          // Use service's universal fallback instead of manual logic
          const serviceInstance = languageDetectionService as any;
          const fallbackResult = serviceInstance.getUniversalFallbackLanguage?.(browserLanguages) || {
            language: { language_code: 'en', language_id: '11703', priority: 1 },
            country: 'US'
          };
          
          const fallbackContext = {
            countryCode: fallbackResult.country, // 🔧 FIX: Use detected country even on localhost
            browserLanguages,
            selectedLanguage: fallbackResult.language,
            fallbackToFirstLanguage: true
          };
          
          setLanguageContext(fallbackContext);
          setIsInitialized(true);
          
          console.log(`${isMobile ? '📱' : '💻'}${isLocalhost ? '🏠' : '🌐'} Universal language fallback applied:`, fallbackContext);
        } catch (fallbackError) {
          // Ultimate fallback if service fails
          const ultimateFallback = {
            countryCode: 'US',
            browserLanguages: ['en'],
            selectedLanguage: { language_code: 'en', language_id: '11703', priority: 1 },
            fallbackToFirstLanguage: true
          };
          
          setLanguageContext(ultimateFallback);
          setIsInitialized(true);
          
          console.log('🆘 ULTIMATE FALLBACK: English used as last resort');
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Only initialize if not already initialized
    if (!isInitialized && !languageDetectionService.isLanguageDetectionInitialized()) {
      // 🔧 MOBILE FIX: Reduced delay for localhost mobile
      const initDelay = (isMobile && !isLocalhost) ? 100 : 0;
      
      if (initDelay > 0) {
        setTimeout(initializeLanguage, initDelay);
      } else {
        initializeLanguage();
      }
    } else if (languageDetectionService.isLanguageDetectionInitialized()) {
      // If service is already initialized, get the context
      const existingContext = languageDetectionService.getCurrentLanguageContext();
      setLanguageContext(existingContext);
      setIsInitialized(true);
    }
  }, [isInitialized, isMobile, isLocalhost]);

  // Refresh language detection
  const refreshLanguageDetection = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🌍 useLanguageDetection: Refreshing language detection...');
      const context = await languageDetectionService.refreshLanguageDetection();
      
      setLanguageContext(context);
      console.log('🌍 useLanguageDetection: Language detection refreshed:', context);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Language detection refresh failed';
      setError(errorMessage);
      console.error('❌ useLanguageDetection: Language detection refresh failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Set manual language
  const setManualLanguage = useCallback((languageCode: string, languageId: string) => {
    try {
      console.log(`🌍 useLanguageDetection: Setting manual language: ${languageCode} (${languageId})`);
      
      languageDetectionService.setLanguage(languageCode, languageId);
      const updatedContext = languageDetectionService.getCurrentLanguageContext();
      
      setLanguageContext(updatedContext);
      setError(null);
      
      console.log('🌍 useLanguageDetection: Manual language set:', updatedContext);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to set manual language';
      setError(errorMessage);
      console.error('❌ useLanguageDetection: Failed to set manual language:', err);
    }
  }, []);

  // Get current language category ID
  const getCurrentLanguageCategoryId = useCallback((): string | null => {
    return languageDetectionService.getCurrentLanguageCategoryId();
  }, []);

  // Get current language code
  const getCurrentLanguageCode = useCallback((): string | null => {
    return languageDetectionService.getCurrentLanguageCode();
  }, []);

  return {
    languageContext,
    isLoading,
    error,
    refreshLanguageDetection,
    setManualLanguage,
    getCurrentLanguageCategoryId,
    getCurrentLanguageCode,
    isInitialized
  };
}; 